<template>
    <div data-testid="ai-info-table" class="ai-info-table d-flex flex-column gap-6">
        <!-- Title -->
        <div class="d-flex align-center" v-if="!hideHeader">
            <PageHeader 
                :title="$nova.labelsName('ai_info_table', 'threshold_label', 'AI Settings')"
                :loading="loading"
                @refresh="refreshData"
            />
        </div>
        <!-- Count Section and Table -->
        <v-card class="pa-4 d-flex flex-column gap-3" flat outlined>
            <div data-testid="ai-info-table__stats-header" class="d-flex align-center gap-4 align-self-start" v-if="!editable">
                <CountSection class="gap-120"
                    data-testid="ai-info-table__features-count" 
                    :count="tableData.length" 
                    :networkLabel="'Features'" 
                    :countLabel="'Count'" 
                />
                <CountSection 
                    data-testid="ai-info-table__features-active" 
                    class="success--text gap-120 active-feature-count"
                    :count="tableData.filter(item => item.super_admin_enabled === true).length" 
                    :networkLabel="'Features'" 
                    :countLabel="'Active'" 
                />
                <CountSection 
                    data-testid="ai-info-table__features-inactive" 
                    class="gap-120"
                    :count="tableData.filter(item => item.super_admin_enabled === false).length" 
                    :networkLabel="'Features'" 
                    :countLabel="'Inactive'" 
                />
            </div>
            <!-- Show table (handles both data, loading, and empty states) -->
            <lb-datatablenew
                data-testid="ai-info-table__data-table"
                :headers="headersWithSNo"
                :items="loading ? [] : displayData"
                :loading="false"
                class="elevation-0 aiTableData"
                :enableslot="enabledSlots"
                :perpage="10"
            >
                <template v-slot:no-data>
                    <div v-if="loading" class="width-100 py-2">
                        <v-skeleton-loader
                            type="table-row-divider@6"
                            class="elevation-0"
                            width="100%"
                        ></v-skeleton-loader>
                    </div>
                    <div v-else class="text-center grey--text title pa-6">
                        <div class="mb-3">No AI features in this client's ai_feature_center</div>
                        <v-btn
                            v-if="editable && clientId"
                            color="primary"
                            small
                            :loading="initializing"
                            @click="initializeFeatures"
                        >
                            Initialize from master
                        </v-btn>
                    </div>
                </template>
                <template v-slot:name="{ item }">
                    <span data-testid="ai-info-table__name-text">{{ item.name }}</span>
                </template>
                <template v-slot:model="{ item }">
                    <v-chip data-testid="ai-info-table__model-chip" class="caccounting--text v-chip v-chip--label theme--light v-size--default lightpurple">
                        <v-icon data-testid="ai-info-table__model-icon" left small>mdi-cube-outline</v-icon>
                        <span>{{ item.model }}</span>
                    </v-chip>
                </template>
                <template v-slot:provider="{ item }">
                    <v-chip data-testid="ai-info-table__provider-chip" class="caccounting--text v-chip v-chip--label theme--light v-size--default lightpurple">
                        <v-icon data-testid="ai-info-table__provider-icon" left small>mdi-cube-outline</v-icon>
                        <span>{{ item.provider }}</span>
                    </v-chip>
                </template>
                <template v-slot:threshold="{ item }">
                    <div data-testid="ai-info-table__threshold-container" v-if="typeof item.threshold === 'object' && item.threshold !== null">
                        <v-chip data-testid="ai-info-table__total-threshold-chip" class="ma-1" small color="teal lighten-4" text-color="black">
                            <span>Total: {{ item.threshold.total }}</span> <v-icon data-testid="ai-info-table__total-threshold-icon" right small
                                :color="item.limit_check_enabled === true ? 'success' : 'error'"> {{
                                    item.limit_check_enabled === true ? 'mdi-check-circle-outline' : 'mdi-close-circle-outline'
                                }}</v-icon>
                        </v-chip>
                        <v-chip data-testid="ai-info-table__hour-threshold-chip" class="ma-1" small color="light-green lighten-4" text-color="black"
                            v-if="item.threshold.hour">
                            <span>Per Hour: {{ item.threshold.hour }}</span> <v-icon data-testid="ai-info-table__hour-threshold-icon" right small
                                :color="item.hour_check_enabled === true ? 'success' : 'error'"> {{ item.hour_check_enabled
                                    === true ? 'mdi-check-circle-outline' : 'mdi-close-circle-outline' }}</v-icon>
                        </v-chip>
                        <v-chip data-testid="ai-info-table__minute-threshold-chip" class="ma-1" small color="indigo lighten-4" text-color="black" v-if="item.threshold.minute">
                            <span>Per Minute: {{ item.threshold.minute }}</span> <v-icon data-testid="ai-info-table__minute-threshold-icon" right small
                                :color="item.minute_check_enabled === true ? 'success' : 'error'"> {{
                                    item.minute_check_enabled === true ? 'mdi-check-circle-outline' : 'mdi-close-circle-outline'
                                }}</v-icon>
                        </v-chip>
                    </div>
                    <div data-testid="ai-info-table__simple-threshold" v-else>
                        <v-chip data-testid="ai-info-table__simple-threshold-chip" class="ma-1" small color="grey lighten-2" text-color="black">
                            <span>{{ item.threshold }}</span>
                        </v-chip>
                    </div>
                </template>
                <template v-slot:platform_admin_enabled="{ item }">
                    <StatusIndicator
                        data-testid="ai-info-table__platform-admin-status"
                        :variant="item.platform_admin_enabled ? STATUS_VARIANTS.APPROVED : STATUS_VARIANTS.REJECTED"
                        :custom-label="item.platform_admin_enabled ? 'Enabled' : 'Disabled'"
                        size="sm"
                    />
                </template>
                <template v-slot:super_admin_enabled="{ item }">
                    <StatusIndicator
                        data-testid="ai-info-table__super-admin-status"
                        :variant="item.super_admin_enabled ? STATUS_VARIANTS.APPROVED : STATUS_VARIANTS.REJECTED"
                        :custom-label="item.super_admin_enabled ? 'Enabled' : 'Disabled'"
                        size="sm"
                    />
                </template>
                <template v-slot:prompt_variants_data="{ item }">
                    <div data-testid="ai-info-table__prompt-variants-container" v-if="Array.isArray(item.prompt_variants_data) && item.prompt_variants_data.length && editable">
                        <div data-testid="ai-info-table__prompt-variants-group" column>
                            <v-chip data-testid="ai-info-table__prompt-variant-chip" v-for="(variant, i) in item.prompt_variants_data" :key="i" small class="ma-1"
                                color="">
                                <span>{{ variant.type }}</span>
                            </v-chip>
                        </div>
                    </div>
                    <div data-testid="ai-info-table__filtered-prompt-variants" v-else>
                        <div data-testid="ai-info-table__filtered-prompt-variants-group" class="d-flex gap-2">
                            <v-chip data-testid="ai-info-table__filtered-variant-chip"
                                v-for="(variant, i) in item.prompt_variants_data.filter(v => v.type === 'instruction' || v.type === 'output')"
                                :key="i"  class="ru-info-chip" color="">
                                <span>{{ variant.type }}</span>
                            </v-chip>
                        </div>
                    </div>
                </template>
                <template v-slot:description="{ item }">
                    <span data-testid="ai-info-table__description-text">{{ item.description }}</span>
                </template>
                <template v-slot:actions="{ item }">
                    <div data-testid="ai-info-table__actions-container" class="d-flex align-center">
                        <template v-if="editable">
                            <v-tooltip bottom content-class="tooltip-bottom">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-btn data-testid="ai-info-table__edit-button" icon small v-bind="attrs" v-on="on" @click="editItem(item)">
                                        <v-icon data-testid="ai-info-table__edit-icon">mdi-pencil</v-icon>
                                    </v-btn>
                                </template>
                                <span>Edit Feature</span>
                            </v-tooltip>
                        </template>
                        <v-tooltip bottom content-class="tooltip-bottom" v-if="item.platform_admin_enabled">
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn data-testid="ai-info-table__view-button" icon small v-bind="attrs" v-on="on" @click="showDetails(item)">
                                    <img src="/img/icons/PenEditIcon.svg" alt="View Icon">
                                </v-btn>
                            </template>
                            <span>View Details</span>
                        </v-tooltip>
                        <v-tooltip bottom content-class="tooltip-bottom" v-else >
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn data-testid="ai-info-table__view-button" icon small v-bind="attrs" v-on="on">
                                    <v-icon data-testid="ai-info-table__view-icon">mdi-eye</v-icon>
                                </v-btn>
                            </template>
                            <span>Feature has been disabled, Please contact your administrator for more information.</span>
                        </v-tooltip>
                    </div>
                </template>
            </lb-datatablenew>
        </v-card>

        <AiFeatureDialog 
            v-if="showDialog"
            v-model="showDialog"
            :dialog-data="dialogData"
            :dialog-mode="dialogMode"
            :editable="editable"
            :loading="loading"
            :client-id="clientId"
            :client-code="clientCode"
            @refresh="refreshData"
        />
    </div>
</template>

<script>
import CountSection from '../../../../modules/varianceanalysis/views/GlSelection/CountSection.vue';
import StatusIndicator from '@/modules/varianceanalysis/components/StatusIndicator.vue';
import AiFeatureDialog from './AiFeatureDialog.vue';
import { STATUS_VARIANTS } from '@/modules/varianceanalysis/utils/statusConstants.js';
import { POP_MODEL } from '@/modules/admin/utils/constants.js';
import PageHeader from '../../../varianceanalysis/components/PageHeader.vue';

export default {
    name: 'AiInfoTable',
    components: {
        CountSection,
        StatusIndicator,
        AiFeatureDialog,
        PageHeader
    },
    props: {
        apiUrl: {
            type: String,
            default: '/v2/client/get-all-ai-featuresbyclient'
        },
        editable: {
            type: Boolean,
            default: true
        },
        clientId: {
            type: String,
            required: true
        },
        clientCode: {
            type: Number,
            default: 0
        },
        filters: {
            type: Object,
            default: () => ({})
        },
        aiHeaders: {
            type: Array,
            default: () => ([
                { text: 'Name', value: 'name' },
                { text: 'Description', value: 'description' },
                { text: 'Model', value: 'model' },
                { text: 'Provider', value: 'provider' },
                { text: 'Threshold', value: 'threshold' },
                { text: 'Platform admin', value: 'platform_admin_enabled' },
                { text: 'Super admin', value: 'super_admin_enabled' },
                { text: 'Prompt variants', value: 'prompt_variants_data' },
                { text: 'Actions', value: 'actions', sortable: false },
            ])
        },
        loadingAI: {
            type: Boolean,
            default: false
        },
        hideHeader: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            POP_MODEL, // Make POP_MODEL available to template
            STATUS_VARIANTS,
            client: this.$store.state.client,
            tableData: [],
            loading: false,
            initializing: false,
            error: '',
            showDialog: false,
            dialogMode: POP_MODEL.VIEWER,
            dialogData: {
                prompt_variants_data: []
            },
        };
    },

    created() {
        // Wait for clientId — avoid querying session (platform admin) DB by mistake
        if (this.clientId) this.refreshData();
    },
    watch: {
        clientId: {
            immediate: false,
            handler(id, prev) {
                if (id !== prev) this.refreshData();
            },
        },
    },
    computed: {
        displayData() {
            return this.tableData.map((item, index) => ({
                ...item,
                sno: index + 1
            }));
        },
        headersWithSNo() {
            return [
                { text: 'S.No', value: 'sno', sortable: false, width: '50px' },
                ...this.aiHeaders
            ];
        },
        enabledSlots() {
            return ['name', 'model', 'provider', 'threshold', 'platform_admin_enabled', 'super_admin_enabled', 'prompt_variants_data', 'description', 'actions', 'no-data'];
        },
    },
    methods: {
        refreshData() {
            this.GetAIData();
        },
        async GetAIData() {
            if (!this.clientId) {
                this.tableData = [];
                this.error = '';
                return;
            }
            this.loading = true;
            this.error = '';
            try {
                // Always pass the entity client id — never fall back to session tenant
                const payload = { client: this.clientId, ...this.filters };
                const response = await this.axios.post(this.apiUrl, payload);
                if (response.data && response.data.status === 'success') {
                    this.tableData = (response.data.features || [])
                } else {
                    this.tableData = [];
                    this.error = response.data.message || [];
                    this.error = response.data?.message || 'No data found';
                }
            } catch (err) {
                this.tableData = [];
                this.error = err.message || 'Error fetching data'
                console.log(this.error, "error");
            } finally {
                this.loading = false;
            }
        },
        async initializeFeatures() {
            if (!this.clientId || this.initializing) return;
            this.initializing = true;
            try {
                await this.$store.dispatch('admin/initializeClientAIFeatures', {
                    axiosInstance: this.axios,
                    clientId: this.clientId,
                });
                await this.GetAIData();
            } catch (err) {
                this.error = err?.message || 'Failed to initialize AI features';
                this.$store.commit('sbError', this.error);
            } finally {
                this.initializing = false;
            }
        },
        showDetails(item) {
            this.dialogData = JSON.parse(JSON.stringify(item));
            this.dialogMode = POP_MODEL.VIEWER;
            this.showDialog = true;
        },
        editItem(item) {
            if (!this.editable) return;
            this.dialogData = JSON.parse(JSON.stringify(item));
            this.dialogMode = POP_MODEL.EDITOR;
            this.showDialog = true;
        },
    }
};
</script>

<style scoped>
.aiTableData > table > thead{
    background-color: red;
}
.active-feature-count {
    border: 1px solid var(--status-green) !important;
}
::v-deep .active-feature-count .count-label{
    color: var(--green-800) !important;
}
</style>
